import React from 'react'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { getDKTToken, atlanticLogin } from '../redux/actions/SSO'
import { getUser } from '../redux/actions/user'
import {
  LOCAL_STORAGE_KEYS,
  ssoErrorRedirectUrl,
  ssoLogoutRedirectUrl,
} from '@/utils/constants'
import { refreshToken } from '@/redux/services/user'
import {
  SSO_HANDLED_KEY,
  SSO_LOGIN_FAILED_KEY,
  SSO_ERROR_PAGE_UID,
  buildSsoErrorPageUrl,
  extractAtlanticLoginErrorCode,
  isOnSsoPage,
  isSsoErrorPageRoute,
  logAtlanticLoginFailure,
} from '@/utils/ssoErrors'
import Loader from './Loader/Loader'

type Tprops = {
  children?: any
  getDKTToken: any
  atlanticLogin: any
  user: any
  getUser: any
}

const mapStateToProps = (state) => ({
  user: state.user,
})

const mapDispatchToProps = {
  getDKTToken: getDKTToken,
  atlanticLogin: atlanticLogin,
  getUser: getUser,
}

const SSO_LOGOUT_REDIRECT_PENDING_KEY = 'sso_logout_redirect_pending'
const SSO_LOGIN_IN_PROGRESS_KEY = 'sso_login_in_progress'
const REQUIRED_USER_FIELDS = ['firstName', 'lastName', 'phoneNumber', 'gender']

const getMissingUserFields = (userData) =>
  REQUIRED_USER_FIELDS.filter((field) => {
    const value = userData?.[field]
    return typeof value !== 'string' || value.trim() === ''
  })

/** OAuth `code` is in the URL before Next router query is hydrated. */
const getOAuthCodeFromLocation = () => {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  if (params.get('product')) return null
  return params.get('code')
}

const usesExternalIdpLogout = (
  connexionType: string | null,
  userType: string | null,
  store?: string
) =>
  userType === 'SSO' &&
  (connexionType === 'decathlon' || store === 'cultura' || store === 'virgin')

const SSO = ({
  children,
  getDKTToken,
  atlanticLogin,
  user,
  getUser,
}: Tprops) => {
  const router = useRouter()
  const isFunnel = ['/information', '/shipping', '/payment'].includes(
    router.pathname
  )
  const currentLocale = router.locale
  const isThermor = process.env.SSO_TYPE === 'thermor'
  const wasLoggedInRef = useRef<boolean | undefined>(undefined)
  const ssoSessionRef = useRef<{
    connexionType: string | null
    userType: string | null
    store?: string
  } | null>(null)
  const [ssoLoginInProgress, setSsoLoginInProgress] = useState(false)
  const [ssoLoginFailed, setSsoLoginFailed] = useState(false)

  const oauthCodeInUrl = getOAuthCodeFromLocation()
  const oauthCode =
    oauthCodeInUrl || (router.isReady ? (router.query?.code as string) : null)
  const isSsoLoginCallback = Boolean(
    oauthCode && !(router.isReady && router.query?.product)
  )

  /** True once this exact `code` has already been processed (e.g. page reload). */
  const ssoCodeAlreadyHandled =
    typeof window !== 'undefined' &&
    Boolean(oauthCode) &&
    sessionStorage.getItem(SSO_HANDLED_KEY) === oauthCode

  /**
   * `ssoLoginInProgress` is React state and is therefore reset to `false` if the
   * component remounts mid-callback (e.g. when the Next router becomes ready).
   * The durable truth lives in sessionStorage and survives the remount, so we
   * read it too. It is gated on an OAuth `code` being present so that a stale
   * flag from a previously interrupted login can never lock a code-less page in
   * the loader.
   */
  const ssoLoginInProgressDurable =
    ssoLoginInProgress ||
    (Boolean(oauthCode) &&
      typeof window !== 'undefined' &&
      sessionStorage.getItem(SSO_LOGIN_IN_PROGRESS_KEY) === '1')

  /**
   * Homepage exception: the IdP redirects to `/` with `?code=` (+ other query
   * params) to perform the login. An SSO login is still "resolving" while it is
   * in progress, or while a fresh callback code is present that has neither
   * failed nor been handled yet. We must wait for it instead of redirecting the
   * guest away. Once it fails / is stale / completes, this becomes false so we
   * either redirect (guest) or render the page (logged in).
   */
  const isSsoLoginResolving =
    !user.isLoggedIn &&
    (ssoLoginInProgressDurable ||
      (isSsoLoginCallback && !ssoLoginFailed && !ssoCodeAlreadyHandled))

  const shouldDeferGuestRedirect = !router.isReady || isSsoLoginResolving

  const isOnSsoLogoutRedirectPage =
    typeof window !== 'undefined' &&
    Boolean(ssoLogoutRedirectUrl) &&
    window.location.href.includes(ssoLogoutRedirectUrl as string)

  const shouldBlockProtectedPage = (() => {
    if (!ssoLogoutRedirectUrl || isOnSsoLogoutRedirectPage) return false
    // Actively resolving an SSO login → show the loader, never the page.
    if (isSsoLoginResolving) return true
    // Still restoring the initial session → outcome unknown yet.
    if (!router.isReady || user.loading || !user.sessionChecked) return true
    // Guest about to be redirected → don't flash the page.
    if (!user.isLoggedIn) return true
    return false
  })()

  const hasStoredAuthToken = () => {
    if (typeof window === 'undefined') return false
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN)
    return token != null && token !== 'null'
  }

  useEffect(() => {
    if (sessionStorage.getItem(SSO_LOGIN_IN_PROGRESS_KEY) === '1') {
      setSsoLoginInProgress(true)
    }
  }, [])

  const redirectToSsoLogoutUrl = () => {
    sessionStorage.removeItem(SSO_LOGOUT_REDIRECT_PENDING_KEY)
    window.location.assign(ssoLogoutRedirectUrl as string)
  }

  const redirectToSsoErrorUrl = (reason: string | null) => {
    sessionStorage.setItem(SSO_LOGIN_FAILED_KEY, '1')
    sessionStorage.removeItem(SSO_HANDLED_KEY)
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN)
    window.location.assign(buildSsoErrorPageUrl(reason))
  }

  const isOnSsoErrorPage =
    router.isReady &&
    (isSsoErrorPageRoute(router.pathname, router.query.uid) ||
      router.asPath.includes(`/pages/${SSO_ERROR_PAGE_UID}`))

  const shouldSkipAutoSsoRedirect = () =>
    isOnSsoErrorPage ||
    (typeof window !== 'undefined' &&
      sessionStorage.getItem(SSO_LOGIN_FAILED_KEY) === '1') ||
    (typeof window !== 'undefined' && isOnSsoPage(window.location.href))

  useEffect(() => {
    if (!ssoLogoutRedirectUrl) return
    if (shouldDeferGuestRedirect) return
    if (isOnSsoLogoutRedirectPage) return
    if (shouldSkipAutoSsoRedirect()) return

    if (!hasStoredAuthToken()) {
      redirectToSsoLogoutUrl()
      return
    }

    if (user.loading) return
    if (!user.sessionChecked) return

    if (!user.isLoggedIn) {
      redirectToSsoLogoutUrl()
    }
  }, [
    user.isLoggedIn,
    user.loading,
    user.sessionChecked,
    router.isReady,
    router.query.uid,
    isSsoLoginCallback,
    ssoLoginInProgress,
    ssoLoginFailed,
    oauthCodeInUrl,
    isOnSsoLogoutRedirectPage,
  ])

  useEffect(() => {
    if (user.isLoggedIn) {
      ssoSessionRef.current = {
        connexionType: localStorage.getItem(LOCAL_STORAGE_KEYS.CONNEXION_TYPE),
        userType: localStorage.getItem(LOCAL_STORAGE_KEYS.USER_TYPE),
        store: process.env.STORE,
      }
    }
  }, [user.isLoggedIn, user?.user])

  useEffect(() => {
    const isLoggedIn = !!user.isLoggedIn
    const wasLoggedIn = wasLoggedInRef.current

    if (!ssoLogoutRedirectUrl) return
    if (user.loading) return
    if (!user.sessionChecked) return
    if (shouldDeferGuestRedirect) return

    if (wasLoggedIn === true && !isLoggedIn) {
      sessionStorage.setItem(SSO_LOGOUT_REDIRECT_PENDING_KEY, '1')
      const { connexionType, userType, store } = ssoSessionRef.current || {}
      if (!usesExternalIdpLogout(connexionType, userType, store)) {
        redirectToSsoLogoutUrl()
      }
    }

    wasLoggedInRef.current = isLoggedIn
  }, [
    user.isLoggedIn,
    user.loading,
    user.sessionChecked,
    router.isReady,
    isSsoLoginCallback,
    ssoLoginInProgress,
    oauthCodeInUrl,
  ])

  useEffect(() => {
    if (!router.isReady && !oauthCodeInUrl) return

    if (
      router.isReady &&
      isThermor &&
      ssoErrorRedirectUrl &&
      router?.query?.error &&
      !router?.query?.product
    ) {
      redirectToSsoErrorUrl(null)
      return
    }

    const code = (
      router.isReady ? (router.query.code as string) : oauthCodeInUrl
    ) as string | null

    if (
      !code ||
      (router.isReady && router.query?.product) ||
      localStorage?.getItem(LOCAL_STORAGE_KEYS.TOKEN) === 'null'
    ) {
      return
    }

    if (sessionStorage.getItem(SSO_HANDLED_KEY) === code) return

    sessionStorage.setItem(SSO_HANDLED_KEY, code)
    sessionStorage.setItem(SSO_LOGIN_IN_PROGRESS_KEY, '1')
    setSsoLoginInProgress(true)
    setSsoLoginFailed(false)

    const localePath = currentLocale !== 'fr' ? currentLocale : ''
    const redirectUrl = isFunnel
      ? localePath
        ? `${process.env.SSO_REDIRECT_URL}${localePath}/shipping`
        : `${process.env.SSO_REDIRECT_URL_ON_FUNNEL}`
      : `${process.env.SSO_REDIRECT_URL}${localePath}`

    const clearSsoLoginInProgress = () => {
      sessionStorage.removeItem(SSO_LOGIN_IN_PROGRESS_KEY)
      setSsoLoginInProgress(false)
    }

    if (isThermor) {
      const handleThermorSSO = async () => {
        try {
          const action = await atlanticLogin(code, redirectUrl)

          if (
            action?.type !== 'ATLANTIC_LOGIN_SUCCESS' ||
            !action?.data?.token
          ) {
            logAtlanticLoginFailure(action)
            redirectToSsoErrorUrl(extractAtlanticLoginErrorCode(action))
            return
          }

          localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN)
          localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, action.data.token)
          localStorage.setItem(LOCAL_STORAGE_KEYS.USER_TYPE, 'SSO')

          const userAction = await getUser()
          if (userAction?.type === 'GET_USER_FAILURE') {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN)
            redirectToSsoErrorUrl(null)
            return
          }

          sessionStorage.removeItem(SSO_LOGIN_FAILED_KEY)

          // Land on the catalog rather than the homepage. Skipped in the funnel
          // and when the account still has to go through informations-check.
          if (
            !isFunnel &&
            getMissingUserFields(userAction?.user).length === 0
          ) {
            router.replace('/shop')
          }
        } catch (error) {
          console.error('Thermor SSO login failed:', error)
          redirectToSsoErrorUrl(null)
        } finally {
          clearSsoLoginInProgress()
        }
      }
      handleThermorSSO()
    } else {
      const dktLogin = async () => {
        try {
          const token = await getDKTToken(code, redirectUrl)
          if (token.type === 'GET_TOKEN_SUCCESS') {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN)
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.CONNEXION_TYPE,
              process.env.SSO_TYPE
            )
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.TOKEN,
              token?.token?.access_token
            )
            localStorage.setItem(LOCAL_STORAGE_KEYS.USER_TYPE, 'SSO')
            await getUser()
          } else {
            sessionStorage.removeItem(SSO_HANDLED_KEY)
            setSsoLoginFailed(true)
          }
        } catch (error) {
          sessionStorage.removeItem(SSO_HANDLED_KEY)
          setSsoLoginFailed(true)
          console.error('DKT SSO login failed:', error)
        } finally {
          clearSsoLoginInProgress()
        }
      }
      dktLogin()
    }
  }, [
    router.isReady,
    router.query?.code,
    router.query?.product,
    router.query?.error,
    oauthCodeInUrl,
  ])

  useEffect(() => {
    const tokenExpireAt = localStorage?.getItem(
      LOCAL_STORAGE_KEYS.TOKEN_EXPIRE_AT
    )
    const connexionType = localStorage?.getItem(
      LOCAL_STORAGE_KEYS.CONNEXION_TYPE
    )

    // only for CIAM - Virgin
    if (tokenExpireAt && connexionType === 'ciam') {
      const refresh = async () => {
        return await refreshToken()
      }
      const currentDate = Math.floor(new Date().getTime() / 1000)
      if (Number(tokenExpireAt) < currentDate) {
        refresh().then((res) => {
          localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, res.access_token)
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
            res.refresh_token
          )
          localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, res.expires_in)
        })
      }
    }
  }, [])

  useEffect(() => {
    if (
      typeof user?.user !== 'undefined' &&
      user?.user !== '' &&
      router.pathname !== '/account/informations-check'
    ) {
      const requiredFields = getMissingUserFields(user?.user)

      if (requiredFields.length > 0 && user.isLoggedIn) {
        router.push('/account/informations-check')
      }
    }
  }, [user?.user])

  if (shouldBlockProtectedPage) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
        }}
      >
        <Loader />
      </div>
    )
  }

  return children
}

export default connect(mapStateToProps, mapDispatchToProps)(SSO)
